/**
 * File: seo-meta.ts
 *
 * Every page's SEO identity — title, description, canonical URL, social card and
 * structured data — in one pure, dependency-free module. Like articles-meta.ts it
 * is imported in TWO places and therefore must not import anything with a DOM or
 * runtime dependency (no `marked`, no vue, no Vite virtual modules):
 *   - at BUILD time by the `seoAssets` and `prerender` plugins in vite.config.ts,
 *     to generate `sitemap.xml` and the static per-article HTML that link
 *     unfurlers and non-rendering crawlers actually read;
 *   - at RUNTIME by seo.ts, to keep the live document head in step with the
 *     router as the SPA navigates.
 *
 * Both callers must agree, so the values live here rather than in either one.
 *
 * Why the build-time half exists at all: www.bunnyquery.com is served as static
 * files from S3 behind a CloudFront custom error response, so EVERY unknown path
 * returns the same SPA shell. Social crawlers (Slack, LinkedIn, X, Facebook,
 * Discord) do not execute JavaScript, so a head written by seo.ts after mount is
 * invisible to them — without the prerendered copies every shared article link
 * unfurls as the generic home page card.
 */

import type { ArticleMeta } from "./articles-meta";

/** Canonical origin. No trailing slash — `abs()` joins the path. */
export const ORIGIN = "https://www.bunnyquery.com";

export const SITE_NAME = "BunnyQuery";
export const PUBLISHER = "Broadway Inc.";

/** Social card. 1200x630, the size Open Graph consumers crop to. */
export const OG_IMAGE = "/ograph.jpg";
export const OG_IMAGE_W = 1200;
export const OG_IMAGE_H = 630;

/** Square mark used as the Organization logo in structured data. */
export const LOGO = "/web-app-manifest-512x512.png";

export const DEFAULT_TITLE = "BunnyQuery | AI Indexed Data Storage";
export const DEFAULT_DESCRIPTION =
    "Upload your documents, spreadsheets, images, notes, and business records. BunnyQuery turns them into usable database, so your AI can answer questions.";

/** Longest description a crawler will render before truncating it itself. */
const DESCRIPTION_LIMIT = 160;

export interface PageSeo {
    /** Full <title>, already suffixed with the site name where appropriate. */
    title: string;
    description: string;
    /** Absolute canonical URL for this page. */
    canonical: string;
    /** Absolute social card URL. */
    image: string;
    imageAlt: string;
    ogType: "website" | "article";
    /**
     * `robots` content. Undefined on indexable pages so no tag is emitted —
     * the absence of the tag is the "index, follow" default, and an explicit
     * tag would only be one more thing to keep correct.
     */
    robots?: string;
    /** ISO-8601 publication date, articles only. */
    published?: string;
    /** JSON-LD graph nodes for this page. */
    jsonLd: Record<string, any>[];
}

/** Absolute URL for a site-root-relative path. */
export function abs(path: string): string {
    return path.startsWith("http") ? path : `${ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Clamp a description to what a result snippet will actually show, cutting on a
 * word boundary rather than mid-word. Article excerpts arrive pre-truncated at
 * 180 characters (see deriveExcerpt), which is past the limit.
 */
export function clampDescription(text: string, limit = DESCRIPTION_LIMIT): string {
    const flat = text.replace(/\s+/g, " ").trim();
    if (flat.length <= limit) return flat;
    const cut = flat.slice(0, limit - 1);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
}

/** `dateSort` (epoch ms, UTC midnight) as a bare ISO date. 0 means undated. */
export function isoDate(dateSort: number): string | undefined {
    return dateSort ? new Date(dateSort).toISOString().slice(0, 10) : undefined;
}

/* ─────────────────────────── structured data ─────────────────────────── */

/**
 * The publisher node, referenced by @id from every other node so the graph
 * describes one organization rather than repeating it per page.
 */
export function organizationNode(): Record<string, any> {
    return {
        "@type": "Organization",
        "@id": `${ORIGIN}/#organization`,
        name: SITE_NAME,
        legalName: PUBLISHER,
        url: `${ORIGIN}/`,
        logo: {
            "@type": "ImageObject",
            url: abs(LOGO),
            width: 512,
            height: 512,
        },
    };
}

export function websiteNode(): Record<string, any> {
    return {
        "@type": "WebSite",
        "@id": `${ORIGIN}/#website`,
        name: SITE_NAME,
        url: `${ORIGIN}/`,
        description: DEFAULT_DESCRIPTION,
        publisher: { "@id": `${ORIGIN}/#organization` },
        inLanguage: "en-US",
    };
}

/**
 * The product itself. `SoftwareApplication` is what makes a SaaS home page
 * eligible for the application rich result; `offers` at price 0 states the free
 * tier, which is the accurate reading of the current plan lineup.
 */
export function softwareNode(): Record<string, any> {
    return {
        "@type": "SoftwareApplication",
        "@id": `${ORIGIN}/#software`,
        name: SITE_NAME,
        url: `${ORIGIN}/`,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description: DEFAULT_DESCRIPTION,
        publisher: { "@id": `${ORIGIN}/#organization` },
        offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
            description: "Free tier with paid usage above the included limits.",
        },
    };
}

export function breadcrumbNode(trail: { name: string; url: string }[]): Record<string, any> {
    return {
        "@type": "BreadcrumbList",
        itemListElement: trail.map((t, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: t.name,
            item: abs(t.url),
        })),
    };
}

export function blogPostingNode(article: ArticleMeta): Record<string, any> {
    const published = isoDate(article.dateSort);
    return {
        "@type": "BlogPosting",
        "@id": `${ORIGIN}/articles/${article.slug}#article`,
        headline: article.title,
        description: clampDescription(article.excerpt || DEFAULT_DESCRIPTION),
        url: `${ORIGIN}/articles/${article.slug}`,
        mainEntityOfPage: { "@type": "WebPage", "@id": `${ORIGIN}/articles/${article.slug}` },
        image: abs(OG_IMAGE),
        ...(published ? { datePublished: published, dateModified: published } : {}),
        author: { "@id": `${ORIGIN}/#organization` },
        publisher: { "@id": `${ORIGIN}/#organization` },
        inLanguage: "en-US",
        isAccessibleForFree: true,
    };
}

/** Wrap nodes in a single `@graph` document — one script tag per page. */
export function jsonLdDocument(nodes: Record<string, any>[]): string {
    return JSON.stringify({ "@context": "https://schema.org", "@graph": nodes });
}

/* ────────────────────────────── page SEO ────────────────────────────── */

function base(overrides: Partial<PageSeo> & { canonical: string }): PageSeo {
    return {
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        image: abs(OG_IMAGE),
        imageAlt: DEFAULT_TITLE,
        ogType: "website",
        jsonLd: [],
        ...overrides,
        canonical: abs(overrides.canonical),
    };
}

export function homeSeo(): PageSeo {
    return base({
        canonical: "/",
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        jsonLd: [organizationNode(), websiteNode(), softwareNode()],
    });
}

export function articlesListSeo(): PageSeo {
    return base({
        canonical: "/articles",
        title: `Articles and Release Notes | ${SITE_NAME}`,
        description:
            "Product news, release notes and deep dives on how BunnyQuery indexes your files and answers questions from your own data.",
        jsonLd: [
            organizationNode(),
            {
                "@type": "Blog",
                "@id": `${ORIGIN}/articles#blog`,
                name: `${SITE_NAME} Articles`,
                url: `${ORIGIN}/articles`,
                description: "Product news, release notes and technical write-ups from the BunnyQuery team.",
                publisher: { "@id": `${ORIGIN}/#organization` },
                inLanguage: "en-US",
            },
            breadcrumbNode([
                { name: "Home", url: "/" },
                { name: "Articles", url: "/articles" },
            ]),
        ],
    });
}

export function articleSeo(article: ArticleMeta): PageSeo {
    return base({
        canonical: `/articles/${article.slug}`,
        title: `${article.title} | ${SITE_NAME}`,
        description: clampDescription(article.excerpt || DEFAULT_DESCRIPTION),
        imageAlt: article.title,
        ogType: "article",
        published: isoDate(article.dateSort),
        jsonLd: [
            organizationNode(),
            blogPostingNode(article),
            breadcrumbNode([
                { name: "Home", url: "/" },
                { name: "Articles", url: "/articles" },
                { name: article.title, url: `/articles/${article.slug}` },
            ]),
        ],
    });
}

/**
 * Public pages worth their own listing and snippet. Signed-in application routes
 * are deliberately absent — `seoForRoute` gives anything unlisted the noindex
 * treatment below, which is the safer default as routes are added.
 */
const PUBLIC_ROUTES: Record<string, { path: string; title: string; description: string }> = {
    login: {
        path: "/login",
        title: `Log In | ${SITE_NAME}`,
        description:
            "Log in to BunnyQuery to open your projects, upload files and ask questions about your own data.",
    },
    signup: {
        path: "/signup",
        title: `Sign Up Free | ${SITE_NAME}`,
        description:
            "Create a free BunnyQuery account. Index 100 GB of your own documents, spreadsheets and images, then ask AI about them.",
    },
    forgot: {
        path: "/forgot-password",
        title: `Reset Your Password | ${SITE_NAME}`,
        description: "Reset the password on your BunnyQuery account.",
    },
};

/** Routes that exist but must never reach the index (thin, private or transient). */
const NOINDEX_TITLES: Record<string, string> = {
    "404page": `Page Not Found | ${SITE_NAME}`,
    confirmation: `Confirm Your Email | ${SITE_NAME}`,
    verification: `Email Verification | ${SITE_NAME}`,
    success: `Welcome to ${SITE_NAME}`,
    "change-password": `Change Password | ${SITE_NAME}`,
    "delete-account": `Delete Account | ${SITE_NAME}`,
    "enable-account": `Reactivate Account | ${SITE_NAME}`,
    bye: `Account Closed | ${SITE_NAME}`,
    create: `New Project | ${SITE_NAME}`,
    "delete-service": `Delete Project | ${SITE_NAME}`,
    subscription: `Plan and Billing | ${SITE_NAME}`,
    "account-settings": `Account Settings | ${SITE_NAME}`,
    "my-projects": `My Projects | ${SITE_NAME}`,
    dashboard: `Project Settings | ${SITE_NAME}`,
    users: `Users | ${SITE_NAME}`,
    records: `Records | ${SITE_NAME}`,
    dbfile: `Files | ${SITE_NAME}`,
    agent: `Chat | ${SITE_NAME}`,
};

/**
 * SEO for a resolved route name. `article` is not handled here — its values come
 * from the fetched article, so the detail view calls `articleSeo` itself once the
 * metadata lands.
 *
 * Unknown names fall through to noindex rather than to the home page defaults:
 * a new private route that nobody remembers to list stays out of the index, and
 * the mistake shows up as a missing listing rather than as a duplicate of `/`.
 */
export function seoForRoute(name: string, path: string): PageSeo {
    if (name === "home") return homeSeo();
    if (name === "articles") return articlesListSeo();

    // The article detail view refines this with the real title, excerpt and
    // BlogPosting node once the metadata lands. What matters here is that the
    // canonical is already the article's own URL and that the page is NOT
    // marked noindex while the fetch is in flight.
    if (name === "article") {
        return base({ canonical: path, ogType: "article", jsonLd: [organizationNode()] });
    }

    const pub = PUBLIC_ROUTES[name];
    if (pub) {
        return base({
            canonical: pub.path,
            title: pub.title,
            description: pub.description,
            jsonLd: [organizationNode()],
        });
    }

    return base({
        canonical: path,
        title: NOINDEX_TITLES[name] || DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        robots: "noindex, follow",
        jsonLd: [],
    });
}
